import {findParent} from '../../js/tools';

const Direction = {
  LEFT: 'left',
  RIGHT: 'right',
  UP: 'up',
  DOWN: 'down',
};

export class Marquee {
  elements = {
    root: null,
    movableBox: null,
  };
  state = {
    nextFrameTime: 0,
    timestamp: 0,
    nextMoveItemContainerTimestamp: 0,
  };
  pixelsPerFrame = 5;
  frameDuration = 1000 / 30;
  direction = Direction.RIGHT;
  isHorizontal;
  isVertical;

  constructor(element, options = {}) {
    if (!element) {
      return;
    }

    const { pixelsPerFrame, frameDuration, direction } = options;

    this.elements = {
      root: element,
      movableBox: element.querySelector('.jk-marquee__movable-box'),
      get itemContainers() {
        return Array.from(this.root.querySelectorAll('.jk-marquee__item-container') || []);
      },
      get itemGap() {
        return parseInt(window.getComputedStyle(this.itemContainers[0])?.gap);
      }
    };
    this.pixelsPerFrame = pixelsPerFrame || this.pixelsPerFrame;
    this.frameDuration = frameDuration || this.frameDuration;
    this.direction = direction || this.direction;
    this.isHorizontal = [Direction.LEFT, Direction.RIGHT].includes(this.direction);
    this.isVertical = [Direction.UP, Direction.DOWN].includes(this.direction);
    this.cloneItemContainer(direction);
    this.addAttributes();
    this.run();
  }

  addAttributes() {
    this.elements.itemContainers[0].dataset.key = 'original';
    this.elements.itemContainers[1].dataset.key = 'clone';
  }

  cloneItemContainer() {
    const original = this.elements.itemContainers[0];
    const clone = original.cloneNode(true);

    if (this.isHorizontal) {
      clone.style.left = `${original.offsetWidth + this.elements.itemGap}px`;
    } else {
      clone.style.top = `${original.offsetHeight + this.elements.itemGap}px`;
    }

    this.elements.movableBox.append(clone);
  }

  onAnimationFrame(timestamp) {
    this.state.timestamp = timestamp;
    if (timestamp > this.state.nextFrameTime) {
      this.state.nextFrameTime = timestamp + this.frameDuration;
      this.moveBox();
    }
    this.onAnimationFrame.handle = window.requestAnimationFrame(this.onAnimationFrame.bind(this));
  }

  run() {
    this.log('run');
    window.requestAnimationFrame(this.onAnimationFrame.bind(this));
  }

  moveBox() {
    const { first: firstItemContainer, last: lastItemContainer } = this.getContainersBy2DPosition();

    switch (this.direction) {
      case Direction.LEFT:
        this.elements.movableBox.style.left = `${this.elements.movableBox.offsetLeft - this.pixelsPerFrame}px`;

        if (
          this.isElementVisibleInRoot(lastItemContainer) &&
          !this.doesElementOverlapRootRight(lastItemContainer)
        ) {
          this.moveItemContainer(Direction.RIGHT);
        }
        break;
      case Direction.RIGHT:
        this.elements.movableBox.style.left = `${this.elements.movableBox.offsetLeft + this.pixelsPerFrame}px`;

        if (
          this.isElementVisibleInRoot(firstItemContainer) &&
          !this.doesElementOverlapRootLeft(firstItemContainer)
        ) {
          this.moveItemContainer(Direction.LEFT);
        }
        break;
      case Direction.UP:
        this.elements.movableBox.style.top = `${this.elements.movableBox.offsetTop - this.pixelsPerFrame}px`;

        if (
          this.isElementVisibleInRoot(lastItemContainer) &&
          !this.doesElementOverlapRootBottom(lastItemContainer)
        ) {
          this.moveItemContainer(Direction.DOWN);
        }
        break;
      case Direction.DOWN:
        this.elements.movableBox.style.top = `${this.elements.movableBox.offsetTop + this.pixelsPerFrame}px`;

        if (
          this.isElementVisibleInRoot(firstItemContainer) &&
          !this.doesElementOverlapRootTop(firstItemContainer)
        ) {
          this.moveItemContainer(Direction.UP);
        }
        break;
    }
  }

  getContainersBy2DPosition() {
    let minLeft = Number.POSITIVE_INFINITY;
    let minTop = Number.POSITIVE_INFINITY;
    let first;
    let last;

    for (const container of this.elements.itemContainers) {
      const left = parseInt(window.getComputedStyle(container).left);
      const top = parseInt(window.getComputedStyle(container).top);

      if (!last) {
        last = container;
      }

      if (this.isHorizontal && left < minLeft) {
        minLeft = left;
        first = container;
      }

      if (this.isVertical && top < minTop) {
        minTop = top;
        first = container;
      }

      if (container !== first) {
        last = container;
      }
    }

    return {
      first,
      last,
    }
  }

  isElementVisibleInRoot(element) {
    const rootRect = this.elements.root.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const horizontalCheck = this.isHorizontal && (
      elementRect.left > rootRect.left && elementRect.left < rootRect.right ||
      elementRect.right > rootRect.left && elementRect.right < rootRect.right
    );
    const verticalCheck = this.isVertical && (
      elementRect.top > rootRect.top && elementRect.top < rootRect.bottom ||
      elementRect.bottom > rootRect.top && elementRect.bottom < rootRect.bottom
    );

    if (horizontalCheck || verticalCheck) {
      return true;
    }

    return false;
  }

  doesElementOverlapRootLeft(element) {
    const rootRect = this.elements.root.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (
      elementRect.right > rootRect.left &&
      elementRect.right < rootRect.right &&
      elementRect.left < rootRect.left
    ) {
      return true;
    }

    return false;
  }

  doesElementOverlapRootRight(element) {
    const rootRect = this.elements.root.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (
      elementRect.left > rootRect.left &&
      elementRect.left < rootRect.right &&
      elementRect.right > rootRect.right
    ) {
      return true;
    }

    return false;
  }

  doesElementOverlapRootTop(element) {
    const rootRect = this.elements.root.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (
      elementRect.bottom > rootRect.top &&
      elementRect.bottom < rootRect.bottom &&
      elementRect.top < rootRect.top
    ) {
      return true;
    }

    return false;
  }

  doesElementOverlapRootBottom(element) {
    const rootRect = this.elements.root.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (
      elementRect.top > rootRect.top &&
      elementRect.top < rootRect.bottom &&
      elementRect.bottom > rootRect.bottom
    ) {
      return true;
    }

    return false;
  }

  moveItemContainer(direction) {
    // move over the other in a leapfrog manner
    // Note: I've noticed getComputedStyle can return incorrect values after certain style changes.
    // To guard against this, I will need to throttle this function.

    if (this.state.timestamp <= this.state.nextMoveItemContainerTimestamp) {
      return;
    }

    this.log('moveItemContainer', direction);

    this.state.nextMoveItemContainerTimestamp = this.state.timestamp + 1000;

    const { first: firstItemContainer, last: lastItemContainer } = this.getContainersBy2DPosition();
    let left = 0;
    let top = 0;

    switch (direction) {
      case Direction.LEFT:
        left = firstItemContainer.offsetLeft - lastItemContainer.offsetWidth - this.elements.itemGap;
        lastItemContainer.style.left = `${left}px`;
        break;
      case Direction.RIGHT:
        left = lastItemContainer.offsetLeft + lastItemContainer.offsetWidth + this.elements.itemGap;
        firstItemContainer.style.left = `${left}px`;
        break;
      case Direction.UP:
        top = firstItemContainer.offsetTop - lastItemContainer.offsetHeight - this.elements.itemGap;
        lastItemContainer.style.top = `${top}px`;
        break;
      case Direction.DOWN:
        top = lastItemContainer.offsetTop + lastItemContainer.offsetHeight + this.elements.itemGap;
        firstItemContainer.style.top = `${top}px`;
        break;
    }
  }

  log(...message) {
    console.log('[Marquee]', ...message);
  }

  destroy() {
    window.cancelAnimationFrame(this.onAnimationFrame.handle);
  }
}
